/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { RadixSelectControl } from "../../../ui/radix-select";
import { useStoreDispatch } from "../../../../store/hooks";
import { setOpenCustomDailog } from "../../../../store/helpers/helperReducers";
import { IngpeerInterfaces } from "../../../../store/api/deviceConfig/types";
import { useNgX2ConnectionData } from "./hooks";
import { Sheet } from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { Label } from "../../../ui/label";

interface IAddEditNgX2ConnectionProps {
  selectedNgX2Connection?: IngpeerInterfaces;
  modelOpenMode?: string;
}

const AddEditNgX2Connection: React.FC<IAddEditNgX2ConnectionProps> = ({ modelOpenMode = "Add", selectedNgX2Connection }) => {
  const dispatch = useStoreDispatch();
  const { NgX2ConnectionData, setNgX2ConnectionData, isAddNewNgX2Connection, submitHandler, onblureHandler, errMsg, setErrMsg } =
    useNgX2ConnectionData(selectedNgX2Connection);
  const handleChange = (name: string, value: string) => {
    setNgX2ConnectionData({ ...NgX2ConnectionData!, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setOpenCustomDailog(false));
  };

  const isEnableSave =
    !!NgX2ConnectionData?.index && !!NgX2ConnectionData?.name && !(Object.values(errMsg).filter((item) => !!item && item).length > 0);

  const isAdd = modelOpenMode?.toLowerCase() === "add";

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button disabled={!isEnableSave} onClick={submitHandler}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={`${modelOpenMode} NG X2 Connection Peer`}
      footer={footer}
      width="520px"
    >
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Index */}
          <InputField
            label="Index"
            name="index"
            type="number"
            value={NgX2ConnectionData?.index ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            disabled={!isAddNewNgX2Connection}
            min={1}
            max={32}
            placeholder="Enter index"
            required
            autoFocus={true}
          />

          {/* Enable */}
          <div className="space-y-2">
            <Label htmlFor="ng-x2-enable">Enable</Label>
            <RadixSelectControl
              options={[
                { value: "true", label: "true" },
                { value: "false", label: "false" },
              ]}
              value={NgX2ConnectionData?.enable || "false"}
              onValueChange={(value) => {
                handleChange("enable", value);
              }}
              placeholder="Select"
            />
          </div>

          {/* Name */}
          <InputField
            label="Name"
            name="name"
            value={NgX2ConnectionData?.name ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter name"
            required
          />

          {/* Address */}
          <InputField
            label="Address"
            name="address"
            value={NgX2ConnectionData?.address ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter address"
            required
          />

          {/* PlmnId */}
          <InputField
            label="PlmnId"
            name="plmnId"
            type="number"
            value={NgX2ConnectionData?.plmnId ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter PlmnId"
            required
          />

          {/* Port */}
          <InputField
            label="Port"
            name="port"
            type="number"
            value={NgX2ConnectionData?.port ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            min={1}
            max={65535}
            placeholder="Enter port"
            required
          />

          {/* EnodeBID */}
          <InputField
            label="EnodeBID"
            name="eNodeBID"
            type="number"
            value={NgX2ConnectionData?.eNodeBID ?? ""}
            onChange={handleChange}
            onBlur={onblureHandler}
            placeholder="Enter EnodeBID"
            required
          />
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditNgX2Connection;
